import { Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { openThemedTab } from '../../../../utils/theme'

/**
 * A `target="_blank"` link into our own app that doesn't flash white in dark
 * mode.
 *
 * A plain target="_blank" anchor lets the browser create the tab and show its
 * own blank (white) surface until the destination document manages a first
 * paint - and that first paint is gated on index.html's render-blocking
 * stylesheets, so it is nowhere near instant. Measured against this build with
 * a 1.5s stylesheet: a plain anchor's tab produced no painted frame at all for
 * ~1.57s, while routing the same navigation through openThemedTab put a
 * correctly themed frame up at ~40ms.
 *
 * The theme is read from redux here rather than accepted as a prop on purpose.
 * The previous round of this fix threaded a `theme` prop down to the incident
 * table's cells, and the My Incidents listing never passed it - so those links
 * silently kept the old white-flash behaviour. Reading it at the point of use
 * means no call site can forget.
 */
const ThemedTabLink = ({ to, children, ...rest }) => {
    const { theme } = useSelector((state) => state?.sideBar)

    const handleClick = (event) => {
        // Modifier / non-primary clicks mean "open in a background tab" (or a
        // new window). Those are the browser's to handle: hijacking them would
        // force the tab to the foreground, and an unseen background tab can't
        // flash at the user anyway.
        if (
            event.defaultPrevented ||
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey
        ) {
            return
        }

        event.preventDefault()
        openThemedTab(to, theme)
    }

    return (
        // rest first: the props below are the whole point of this component,
        // so a caller can style it but can't accidentally spread away the
        // themed-open behaviour.
        <Link
            {...rest}
            to={to}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleClick}
        >
            {children}
        </Link>
    )
}

export default ThemedTabLink
